import { supabase } from '@/integrations/supabase/client';

export interface EscrowAccount {
  id: string;
  seller_id: string;
  // Both naming conventions supported for compatibility
  balance_available?: number;
  balance_pending?: number;
  balance_released?: number;
  available_balance: number;
  pending_balance: number;
  total_released: number;
  created_at: string;
  updated_at: string;
}

export interface EscrowTransaction {
  id: string;
  order_id: string;
  seller_id: string;
  buyer_id: string;
  amount: number;
  status: string;
  type: string;
  transaction_type?: string;
  description?: string;
  released_at?: string;
  created_at: string;
}

export const escrowService = {
  // Get seller's escrow account
  async getSellerEscrowAccount(sellerId: string): Promise<EscrowAccount | null> {
    const { data, error } = await supabase
      .from('escrow_accounts')
      .select('*')
      .eq('seller_id', sellerId)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching escrow account:', error);
      return null;
    }
    
    return data as EscrowAccount;
  },

  // Get escrow transactions for a user
  async getUserTransactions(userId: string, role: 'seller' | 'buyer'): Promise<EscrowTransaction[]> {
    const column = role === 'seller' ? 'seller_id' : 'buyer_id';
    
    const { data, error } = await supabase
      .from('escrow_transactions')
      .select('*')
      .eq(column, userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
    
    return (data || []) as EscrowTransaction[];
  },

  // Hold funds in escrow when order is placed
  async holdFunds(orderId: string, buyerId: string, sellerId: string, amount: number): Promise<boolean> {
    const { data, error } = await supabase
      .rpc('hold_funds', {
        p_order_id: orderId,
        p_buyer_id: buyerId,
        p_seller_id: sellerId,
        p_amount: amount
      });
    
    if (error) {
      console.error('Error holding funds in escrow:', error);
      return false;
    }
    
    return data === true;
  },

  // Release funds when buyer confirms delivery
  async releaseFunds(orderId: string): Promise<boolean> {
    const { data, error } = await supabase
      .rpc('release_funds', {
        p_order_id: orderId
      });
    
    if (error) {
      console.error('Error releasing funds:', error);
      return false;
    }
    
    return data === true;
  },

  // Buyer confirms order delivery (for self-delivered orders)
  async confirmDelivery(orderId: string, buyerId: string): Promise<boolean> {
    // First verify the order belongs to this buyer
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .select('id, buyer_id, status, payment_status, tracking_number')
      .eq('id', orderId)
      .eq('buyer_id', buyerId)
      .single();
    
    if (orderError || !order) {
      console.error('Error fetching order for confirmation:', orderError);
      return false;
    }

    // Only allow confirmation for paid orders that are shipped
    if (order.payment_status !== 'completed') {
      console.error('Cannot confirm unpaid order');
      return false;
    }

    if (order.status !== 'shipped' && order.status !== 'confirmed') {
      console.error('Order must be shipped or confirmed to confirm delivery');
      return false;
    }

    // Release funds through the RPC function
    return await escrowService.releaseFunds(orderId);
  },

  // Request withdrawal (for sellers)
  async requestWithdrawal(sellerId: string, amount: number, bankAccountId?: string): Promise<string | null> {
    const { data, error } = await supabase
      .rpc('request_escrow_withdrawal', {
        p_seller_id: sellerId,
        p_amount: amount,
        p_bank_account_id: bankAccountId
      });
    
    if (error) {
      console.error('Error requesting withdrawal:', error);
      return null;
    }
    
    return data;
  }
};
